import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, FileImage, FileArchive, File as FileIcon, ExternalLink,
  Plus, Loader2, Library as LibraryIcon, ArrowUpRight,
} from "lucide-react";
import { fetchLibrary, openProtectedFile, fetchValidation, fetchLibraryCategories } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import LibraryFormSheet from "@/components/LibraryFormSheet";

const fmtSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

function DocIcon({ ext, size = 13 }) {
  const e = (ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(e)) {
    return <FileImage size={size} className="text-[#3A2A10] shrink-0" />;
  }
  if (e === "zip") return <FileArchive size={size} className="text-[#3A2A10] shrink-0" />;
  if (e === "pdf") return <FileText size={size} className="text-[#3A2A10] shrink-0" />;
  return <FileIcon size={size} className="text-[#3A2A10] shrink-0" />;
}

/**
 * Related-documents panel for Movement and Watch detail pages.
 *
 * Runs one or two library queries based on the context:
 *   - Movement: docs where `caliber` matches, PLUS (as a second, deduped
 *     section) docs by the same `manufacturer` covering different variants.
 *   - Watch: docs linked directly to the watch id + docs matching the
 *     watch's caliber.
 *
 * Also exposes a quick "Upload to library" shortcut that pre-fills
 * manufacturer/caliber/watch linkage so the user isn't re-typing context.
 */
export default function RelatedDocumentsPanel({
  context,          // "movement" | "watch"
  caliber,          // string — the caliber to match on (required for movement, optional for watch)
  manufacturer,     // string — secondary match for movement context
  watchId,          // string — id of the watch (watch context only)
  heading = "Related Documents",
}) {
  const [exact, setExact] = useState(null);      // docs by caliber
  const [variant, setVariant] = useState(null);  // docs by manufacturer (minus the exact ones)
  const [linked, setLinked] = useState(null);    // docs linked to this watch directly
  const [validation, setValidation] = useState({});
  const [categories, setCategories] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    if (context === "movement" && caliber) {
      try {
        const res = await fetchLibrary({ caliber });
        setExact(res.items || []);
      } catch {
        setExact([]);
      }
      if (manufacturer) {
        try {
          const res = await fetchLibrary({ manufacturer });
          setVariant(res.items || []);
        } catch {
          setVariant([]);
        }
      } else {
        setVariant([]);
      }
    } else if (context === "watch") {
      if (watchId) {
        try {
          const r = await fetchLibrary({ watch_id: watchId });
          setLinked(r.items || []);
        } catch {
          setLinked([]);
        }
      } else {
        setLinked([]);
      }
      if (caliber) {
        try {
          const r = await fetchLibrary({ caliber });
          setExact(r.items || []);
        } catch {
          setExact([]);
        }
      } else {
        setExact([]);
      }
    }
  }, [context, caliber, manufacturer, watchId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchValidation().then(setValidation).catch(() => {}); }, []);
  useEffect(() => { fetchLibraryCategories().then((d) => setCategories(d.categories || [])).catch(() => {}); }, []);

  // De-dupe the "same manufacturer" list by removing entries already shown in exact.
  const exactIds = new Set((exact || []).map((d) => d.id));
  const variantFiltered = (variant || []).filter((d) => !exactIds.has(d.id));

  const linkedIds = new Set((linked || []).map((d) => d.id));
  const calOnly = (exact || []).filter((d) => !linkedIds.has(d.id));

  const loading = (context === "movement" && exact === null) ||
                  (context === "watch" && (exact === null || linked === null));

  const Card = ({ d }) => (
    <div className="flex items-center gap-3 px-3 py-2 bg-[#F9F0D4] hover:bg-[#F4E9C9] transition-colors" data-testid={`related-doc-${d.id}`}>
      <DocIcon ext={d.ext} />
      <div className="min-w-0 flex-1">
        <div className="text-sm text-[#0A0604] truncate" title={d.title}>{d.title}</div>
        <div className="text-[10px] text-[#3A2A10] font-mono-data truncate">
          {[d.category, d.manufacturer, fmtSize(d.size)].filter(Boolean).join(" · ")}
          {d.calibers?.length > 0 && (
            <span> · <span className="text-[#D4AF37]">{d.calibers.slice(0, 3).join(", ")}{d.calibers.length > 3 ? "…" : ""}</span></span>
          )}
        </div>
      </div>
      <button
        onClick={async () => {
          try {
            await openProtectedFile(d.storage_path, d.original_filename);
          } catch (err) {
            toast.error(errorMessage(err, "Could not open document"));
          }
        }}
        className="p-1.5 text-[#3A2A10] hover:text-[#0A0604]"
        aria-label="Open"
        data-testid={`related-doc-open-${d.id}`}
      >
        <ExternalLink size={13} />
      </button>
    </div>
  );

  const Section = ({ label, items }) => (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-2">{label} · {items.length}</div>
      {items.length === 0 ? (
        <div className="text-[11px] text-[#3A2A10] italic border border-dashed border-[#8B6A1F]/35 rounded-sm px-3 py-3 text-center">
          None
        </div>
      ) : (
        <div className="border border-[#8B6A1F]/25 rounded-sm divide-y divide-[#8B6A1F]/20 overflow-hidden">
          {items.map((d) => <Card key={d.id} d={d} />)}
        </div>
      )}
    </div>
  );

  // Pre-fills for quick upload
  const formDefaults = context === "movement"
    ? { defaultCalibers: caliber ? [caliber] : [], defaultManufacturer: manufacturer || "" }
    : { defaultWatchIds: watchId ? [watchId] : [], defaultCalibers: caliber ? [caliber] : [] };

  return (
    <div data-testid={`related-documents-${context}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{heading}</div>
        <div className="flex items-center gap-2">
          <Link
            to="/library"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] border border-[#8B6A1F]/35 rounded-sm px-2.5 py-1"
            data-testid="related-docs-open-library"
          >
            <LibraryIcon size={11} /> Library <ArrowUpRight size={11} />
          </Link>
          <button
            onClick={() => setFormOpen(true)}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#3A2A10] hover:text-[#0A0604] border border-[#8B6A1F]/45 rounded-sm px-2.5 py-1"
            data-testid="related-docs-upload-btn"
          >
            <Plus size={11} /> Upload
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#3A2A10] py-6">
          <Loader2 size={14} className="animate-spin" /> Loading related documents…
        </div>
      ) : context === "movement" ? (
        <>
          <Section label={`Exact caliber · ${caliber || "—"}`} items={exact} />
          {manufacturer && <Section label={`Other variants · ${manufacturer}`} items={variantFiltered} />}
        </>
      ) : (
        <>
          <Section label="Linked to this watch" items={linked || []} />
          {caliber && <Section label={`Related by caliber · ${caliber}`} items={calOnly} />}
        </>
      )}

      <LibraryFormSheet
        open={formOpen}
        onOpenChange={setFormOpen}
        doc={null}
        categories={categories}
        validation={validation}
        {...formDefaults}
        onSaved={() => { setFormOpen(false); load(); }}
      />
    </div>
  );
}
